import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';

import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import {
  notifyFullMode,
  notifyExitFullMode,
  enterFullMode,
  exitFullMode
} from '../../containers/_commons/navigation';
import NavigationHeader from '../../elements/NavigationHeader';

import PropertyFilter from './PropertyFilter';
import SingleDateRangePicker from '../DatePickerWidget/SingleDateRangePicker';

const InlineFilters = React.forwardRef((props, givenRef) => {
  const { translate } = React.useContext(Context);
  const {
    showLabels,
    property,
    availableFilters,
    containerSettings,
    propertySettings,
    dateRange,
    dateRangeSettings,
    dateRangeColumnSettings
  } = props;
  const ownRef = useRef();
  const ref = givenRef || ownRef;
  const refMobile = React.useRef();
  const propertyRef = useRef();
  const {
    isMobile = false,
    header,
    headerSettings,
    containerOnFullScreenSettings,
    headerLocation
  } = props;

  const [navigation, setNavigation] = React.useState('all');

  const goBack = () => {
    setNavigation('all');
    notifyExitFullMode();
    exitFullMode(refMobile);
  };

  const getNavigationProps = (
    key,
    { onClear, onFocus, onChange, onClick, avoidCloseFilter = false, ...rest }
  ) => {
    return {
      onClear: (event) => {
        if (onClear) {
          onClear(event);
        }
        setNavigation(key);
        notifyFullMode();
      },
      onFocus: (event) => {
        if (onFocus) {
          onFocus(event);
        }
        setNavigation(key);
        if (navigation === 'all') {
          notifyFullMode();
          enterFullMode(refMobile);
        }
      },
      onClick: (event) => {
        if (onClick) {
          onClick(event);
        }
        setNavigation(key);
        notifyFullMode();
        enterFullMode(refMobile);
      },
      onChange: (event) => {
        if (onChange) {
          onChange(event);
        }
        if (!Array.isArray(event)) {
          if (!avoidCloseFilter) {
            goBack();
          }
        }
      },
      ...rest
    };
  };

  const elementsWrappers = {
    Property: (e) => {
      return e;
    },
    SingleDateRangePicker: (e) => (
      <Col key="SingleDateRangePicker" {...dateRangeColumnSettings}>
        {e}
      </Col>
    )
  };

  const wrapperRender = elementsWrappers[navigation];

  const elements = {
    Property: (
      <PropertyFilter
        key="Property"
        showLabels={showLabels}
        {...propertySettings}
        {...property}
        isMobile={isMobile}
        getNavigation={isMobile ? { ...getNavigationProps('Property', property) } : null}
        navigation={navigation}
      />
    ),
    SingleDateRangePicker: (
      <SingleDateRangePicker
        showLabel={showLabels}
        {...dateRangeSettings}
        {...dateRange}
        onChange={(date) => {
          dateRange.onChange({
            start: date.start,
            end: date.end,
            flexibleDates: date.flexibleDates,
            availableDays: date.availableDays,
            checkedFlexibleDates: date.checkedFlexibleDates
          });
        }}
        defaultValue={dateRange.value}
      />
    )
  };

  const filtersToDisplay = availableFilters.filter((e) => {
    let iterator = e;
    if (typeof iterator === 'object') {
      iterator = e.type;
    }
    return elements[iterator];
  });

  const propertyFocus = () => {
    propertyRef.current?.focus();
  };

  React.useImperativeHandle(ref, () => ({
    propertyFocus
  }));

  const firstElementRef = useRef(null);
  return !isMobile ? (
    <>
      <Row gutters marginBottom="2" {...containerSettings} focusReferences={ref}>
        {filtersToDisplay.map((ele, index) => {
          let e = ele;
          if (typeof e === 'object') {
            e = e.type;
          }
          const wrapper = elementsWrappers[e];
          const element = wrapper(elements[e]);
          if (index === 0) {
            let temporalElement;
            if (e === 'Property') {
              temporalElement = React.cloneElement(elements[e], {
                inputFocusReferences: firstElementRef
              });

              return wrapper(temporalElement);
            } else {
              return element;
            }
          } else {
            return element;
          }
        })}
      </Row>
    </>
  ) : (
    <Box
      position={navigation === 'all' ? 'relative' : undefined}
      display={undefined}
      flexDirection={undefined}
      focusReferences={refMobile}
    >
      {navigation === 'all' ? (
        <>
          {header}
          <Row gutters marginBottom="2" {...containerSettings}>
            {filtersToDisplay.map((ele, index) => {
              let e = ele;
              if (typeof e === 'object') {
                e = e.type;
              }
              const wrapper = elementsWrappers[e];
              const element = wrapper(elements[e]);
              if (index === 0) {
                let temporalElement;
                if (e === 'Property') {
                  temporalElement = React.cloneElement(elements[e], {
                    inputFocusReferences: firstElementRef
                  });

                  return wrapper(temporalElement);
                } else {
                  return element;
                }
              } else {
                return element;
              }
            })}
          </Row>
          {props.children}
        </>
      ) : (
        <>
          <NavigationHeader
            headerLocation={headerLocation}
            title={translate(`cmp-header-mobile-${navigation.toLowerCase()}`)}
            onClickIcon={goBack}
            styleIcon={{
              transform: 'rotate(90deg)',
              display: 'inline-block'
            }}
            {...headerSettings}
          />
          <Row
            gutters
            {...containerSettings}
            paddingWidth="15px !important"
            {...containerOnFullScreenSettings}
            flex={undefined}
          >
            {navigation === 'Property'
              ? filtersToDisplay
                  .filter((ele) => ele === 'Property')
                  .map((ele, index) => {
                    let e = ele;
                    const wrapper = elementsWrappers[e];
                    const element = wrapper(elements[e]);
                    if (index === 0) {
                      let temporalElement;
                      if (e === 'Property') {
                        temporalElement = React.cloneElement(elements[e], {
                          inputFocusReferences: firstElementRef,
                          goBack
                        });

                        return wrapper(temporalElement);
                      } else {
                        return element;
                      }
                    } else {
                      return element;
                    }
                  })
              : wrapperRender(elements[navigation])}
          </Row>
        </>
      )}
    </Box>
  );
});

InlineFilters.displayName = 'InlineFilters';

InlineFilters.propTypes = {
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(PropTypes.oneOf(['Property', 'SingleDateRangePicker'])),
  containerSettings: PropTypes.objectOf(PropTypes.any),

  propertySettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters state and actions
   */
  property: PropTypes.shape({
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any),
    onClear: PropTypes.func,
    onChange: PropTypes.func
  })
};

InlineFilters.defaultProps = {
  clearButtonDisplay: 'hidden',
  availableFilters: [],
  showLabels: false,
  containerSettings: {
    padding: '1'
  },
  propertySettings: {},
  property: {}
};

export default InlineFilters;
